// Eager slim slice of the calendar component barrel (#16 calendar slim).
//
// The full `./index.js` barrel `export *`s all 11 calendar component files
// (~200KB — the whole week/month/team/team-now/mini-calendar/sidebar/main/
// quick-create UI tree plus the three modals). The unified shell used to drag
// ALL of it into its EAGER chunk by folding the calendar context via
// `sharedLibraries.js` (`import calendar from '@symbo-ls/workspace-calendar'` →
// smbls's mergeSharedLibraries folds the full components+functions+state into
// the boot root context).
//
// Calendar is now a lazy sub-app: the `/calendar` route folds the FULL calendar
// context into the live shell context on first mount (workspace/pages/index.js →
// lazySharedLibPage). So every calendar UI key that route references resolves
// against the folded context — none of the heavy page-body components (CalMain,
// CalSidebar, CalWeekView, CalMonthView, CalTeamView, CalTeamNowView,
// CalMiniCalendar, CalQuickCreate) needs to be eager.
//
// Only THREE component files stay eager, because non-calendar surfaces reference
// their keys BEFORE the /calendar route folds the context — and none of those
// surfaces fold calendar on their own:
//   • EventDetailModal.js — `CalEventDetailModal` is string-extended on a
//     NON-folding surface: the shell-root opener
//     workspace/functions/eventDetailModal.js's `openEventDetailModal`
//     (`content: { extends: 'CalEventDetailModal' }`, a @symbo.ls/windows
//     custom layer) fires on ANY route — the HOME dashboard's "Coming up" feed
//     (AppSidebar/IntranetRows/LinkedResources event cells), OnlineMembers,
//     /chat, and pageContext.selectPageItem's event case. Its only childExtends
//     (`CalInviteeRow`) lives in-file, so it is a self-contained render closure.
//   • ProfileModal.js — `CalProfileModal` is string-extended by the shell-root
//     opener workspace/functions/profileModal.js's `openProfileModal`, fired on
//     ANY route: HOME OnlineMembers/IntranetRows, /chat, canvas team-and-agents
//     + tagContextMenu, and data.js. Its childExtends (CalProfileTabBtn,
//     CalProfileDetailRow) + its bare-keyed CalAvailabilityGrid and the whole
//     CalAvailability* / CalProfileUnavailRow closure all live in-file.
//   • NewBookingModal.js — `CalNewBookingModal` is string-extended the same
//     way as CalEventDetailModal above: the shell-root opener
//     workspace/functions/newBookingModal.js's `openNewBookingModal`
//     (`content: { extends: 'CalNewBookingModal' }`, a @symbo.ls/windows
//     custom layer) fires on ANY route — the person popover's "Meet" action
//     (workspace/components/PersonPopover.js), meet's openSuggestionModal,
//     and every calendar-native opener now route through it too (migrated
//     2026-08-01, mirroring the 2026-06-01 EventDetailModal/ProfileModal
//     move off the old bare-mount + stateKey Modal pattern). Its childExtends
//     (CalEventTypeBtn, CalMeetingModeBtn, CalInviteePill,
//     CalInviteeSuggestion, CalRecurringBtn, CalRecurringDayBtn) + its
//     siblings CalConfirmMoveModal / CalVisibilityBtn all live in-file.
//
// The el.call'd functions these three fire on those non-folding surfaces
// (saveEvent / deleteEvent / updateSharedEventFields from
// the modals; freshBooking from the edit/new-booking paths) ride the companion
// functions/eagerSlice.js. Everything else resolves via the lazy fold.
//
// Main.js (CalMain/CalHeader/CalViewBtn) is deliberately NOT eager. (Its old
// blocker — a static `rowToEvent` import from ../pages/index.js dragging the
// 31KB page module into the boot chunk — is gone: rowToEvent lives in
// functions/calendarUtils.js now.) CalMain is bare-keyed ONLY by pages/index.js
// (which folds), so it rides the lazy chunk with zero risk either way.
// (OnlineMember/OnlineMembers/CalOnlineAvatar/CalOnlineUsersBar that Main.js
// re-exports live in @symbo-ls/workspace-shell, not calendar — already eager.)
//
// calendar/package.json has no `sideEffects: false`, so a re-export through
// `./index.js` would NOT tree-shake (referencing it evaluates every sibling
// source file — the whole barrel). Each key below is therefore `export *`-ed
// from its SOURCE file directly, so only these three modules ride the eager chunk.
export { CalEventDetailModal, CalInviteeRow, CalRsvpButton } from './EventDetailModal.js'
export * from './ProfileModal.js'
export * from './NewBookingModal.js'
